<template>
  <div>
    <BaseTitle :title="$t('content.files_title')" :yearPeriode="titleData" />
    <v-row>
      <!-- Sidebar -->
      <v-col cols="12" md="auto" class="pt-0" style="width: 280px">
        <v-card class="pa-6">
          <div class="d-flex align-center">
            <v-text-field v-model="search.folder" placeholder="Folder" append-icon="mdi-magnify" @keyup="searchFolder" />
            <v-btn
              v-if="(editable && permissionButton('ROLE_FILES_CREATE')) || (editable && permissionButton('ROLE_FILES_LITE_CREATE'))"
              text
              color="primary"
              class="ml-2 btn-action-custom"
              @click="addFolder"
              ><v-icon>mdi-plus</v-icon></v-btn
            >
          </div>
          <div v-show="loadingTree" class="text-center">
            <v-progress-circular indeterminate />
          </div>
          <tree
            ref="tree"
            v-show="!loadingTree"
            :animation="{ expand: { duration: 0 }, collapse: { duration: 0 } }"
            :fields="tree.fields"
            :nodeTemplate="tree.template"
            :allowDragAndDrop="this.editable"
            :allowMultiSelection="true"
            :nodeDragStart="dragStart"
            :nodeDragStop="dragStop"
            :nodeEdited="renameTree"
            @dataSourceChanged="updateTree"
          />
        </v-card>
      </v-col>

      <!-- File Table -->
      <v-col class="pt-0" @dragover="dragover" @dragleave="dragleave" @drop="drop">
        <v-card class="pa-5" :class="{ 'area-active': areaActive }" :data-text="$t('content.file.file_drop_here')">
          <div>
            <ul class="file-breadcrumb">
              <li v-for="item in breadcrumbs" :key="item.id" @click="handlerFileFromBreadcrumb(item)">
                <template v-if="item.label == 'home'">
                  <svg-icon icon="bank" class="primary--text" />
                </template>
                <template v-else>
                  {{ item.label }}
                </template>
              </li>
            </ul>
            <v-data-table
              class="table-custom"
              hide-default-footer
              :headers="tableHeader"
              :loading="table.loading"
              :items="filesWithBack"
              :server-items-length="filesWithBack.length"
              :options.sync="table.options"
              @click:row="handlerFile"
            >
              <template #top>
                <table-header @fetch="getFiles" hide-option-list>
                  <template #left>
                    <v-btn
                      v-if="(editable && permissionButton('ROLE_FILES_CREATE')) || (editable && permissionButton('ROLE_FILES_LITE_CREATE'))"
                      :disabled="!selectedFolderId"
                      color="primary"
                      class="btn-primary-custom mb-2 mb-md-0 mr-md-2"
                      @click="openDialogFile"
                    >
                      {{ $t('upload_file') }}
                    </v-btn>
                  </template>
                </table-header>
              </template>
              <template #item.title="{item}">
                <template v-if="item.isBack">
                  <v-icon>mdi-arrow-up</v-icon>
                  {{ $t('back') }}
                </template>
                <template v-else>
                  <div class="d-flex align-center">
                    <svg-icon icon="note" class="primary--text mr-2" style="flex:none" />
                    <div style="max-width: 200px; overflow-wrap: break-word;word-wrap: break-word;">
                      <a href="javascript:(0)" @click.prevent="previewFile(undefined, { item })">{{ item.title }}</a>
                    </div>
                  </div>
                </template>
              </template>
              <template #item.action="{item}">
                <v-btn
                  v-if="(!item.isBack && permissionButton('ROLE_FILES_VIEW')) || (!item.isBack && permissionButton('ROLE_FILES_LITE_VIEW'))"
                  text
                  @click="viewFile(item)"
                  class="mr-1 btn-action-custom"
                >
                  <svg-icon icon="view" class="primary--text" />
                </v-btn>
              </template>
              <template #item.lock="{item}">
                <svg-icon v-if="!item.isBack & (item.lock === true)" icon="lock" class="red--text" />

                <svg-icon v-if="!item.isBack && item.lock === false" icon="gembok" />
              </template>
              <template #no-data>{{ $t('data_not_available') }}</template>
            </v-data-table>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- File Viewer -->
    <file-viewer :data="preview" v-model="dialogPreview" :hideExtension="true" @onHide="hidePreview" />

    <!-- Dialog Folder -->
    <v-dialog v-model="dialogFolder" width="500" persistent>
      <v-card>
        <v-card-title class="headline secondary white--text">
          {{ $t(formFolder.id ? 'content.edit_folder' : 'content.create_new_folder') }}
        </v-card-title>
        <div class="pa-4">
          <form-group :name="$t('form.folder_name')" :validator="$v.formFolder.name" no-gutter>
            <template slot-scope="{ attrs }">
              <v-text-field
                v-model="formFolder.name"
                v-bind="attrs"
                :label="$t('form.name')"
                @input="$v.formFolder.name.$touch()"
                @blur="$v.formFolder.name.$touch()"
              />
            </template>
          </form-group>
        </div>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn
            color="primary"
            :loading="loadingFolder"
            v-if="permissionButton('ROLE_FILES_CREATE') || permissionButton('ROLE_FILES_LITE_CREATE')"
            text
            @click="saveFolder"
            >{{ $t('save') }}</v-btn
          >
          <v-btn color="primary" text :disabled="loadingFolder" @click="cancelFolder">{{ $t('cancel') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog File -->
    <v-dialog v-model="dialogFile" width="500" persistent>
      <v-card>
        <v-card-title class="headline secondary white--text">
          {{ $t(formFile.id ? 'content.edit_file' : 'content.upload_file') }}
        </v-card-title>
        <div class="pa-4">
          <form-group v-if="!formFile.id || formFile.owner" :name="$t('form.file_name')" :validator="$v.formFile.fileName">
            <template slot-scope="{ attrs }">
              <v-text-field
                v-model="formFile.fileName"
                v-bind="attrs"
                :label="$t('form.file_name')"
                @input="$v.formFile.fileName.$touch()"
                @blur="$v.formFile.fileName.$touch()"
              />
            </template>
          </form-group>
          <form-group :name="$t('form.status')" :validator="$v.formFile.lock">
            <template slot-scope="{ attrs }">
              <v-select v-model="formFile.lock" v-bind="attrs" :label="$t('form.status')" :items="lockItems" @change="$v.formFile.lock.$touch()" />
            </template>
          </form-group>
          <div>
            <v-switch v-model="formFile.scheduled" :label="$t('content.file.file_unlock_with_schedule')" />
            <v-row>
              <v-col>
                <form-group :name="$t('form.date')" :validator="$v.unlockDate" no-gutter>
                  <template slot-scope="{ attrs }">
                    <v-menu
                      ref="pickerDate"
                      v-model="pickerDate"
                      :close-on-content-click="false"
                      :return-value.sync="pickerDate"
                      transition="scale-transition"
                      offset-y
                    >
                      <template #activator="{ on, attrs:attrActivator }">
                        <v-text-field
                          v-model="unlockDate"
                          v-bind="{ ...attrActivator, ...attrs }"
                          v-on="on"
                          readonly
                          :label="$t('form.date')"
                          :disabled="!formFile.scheduled"
                        >
                          <template #append>
                            <svg-icon icon="calendar" class="primary--text mr-3" />
                          </template>
                        </v-text-field>
                      </template>
                      <v-date-picker v-model="unlockDate" :locale="$i18n.locale">
                        <v-spacer />
                        <v-btn text color="primary" @click="pickerDate = false">{{ $t('cancel') }}</v-btn>
                        <v-btn text color="primary" @click="$refs.pickerDate.save(unlockDate)">{{ $t('ok') }}</v-btn>
                      </v-date-picker>
                    </v-menu>
                  </template>
                </form-group>
              </v-col>
              <v-col>
                <form-group :name="$t('form.time')" :validator="$v.unlockTime" no-gutter>
                  <template slot-scope="{ attrs }">
                    <v-menu
                      ref="pickerTime"
                      v-model="pickerTime"
                      transition="scale-transition"
                      offset-y
                      :close-on-content-click="false"
                      :return-value.sync="pickerTime"
                    >
                      <template v-slot:activator="{ on, attrs: attrActivator }">
                        <v-text-field
                          v-model="unlockTime"
                          v-bind="{ ...attrActivator, ...attrs }"
                          v-on="on"
                          readonly
                          :label="$t('form.time')"
                          :disabled="!formFile.scheduled"
                        >
                          <template #append>
                            <svg-icon icon="clock" class="primary--text mr-3" />
                          </template>
                        </v-text-field>
                      </template>
                      <v-time-picker v-model="unlockTime" :locale="$i18n.locale" format="24hr" @click:minute="$refs.pickerTime.save(unlockTime)" />
                    </v-menu>
                  </template>
                </form-group>
              </v-col>
            </v-row>
          </div>
          <form-group v-if="!formFile.id" :name="$t('form.file')" :validator="$v.formFile.file" no-gutter>
            <template slot-scope="{ attrs }">
              <v-file-input
                v-model="formFile.file"
                v-bind="attrs"
                :label="$t('form.file')"
                accept=".jpg,.jpeg,.png,.pdf,.mp4,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.mp3,.sb3"
                @input="$v.formFile.file.$touch()"
                @blur="$v.formFile.file.$touch()"
              />
            </template>
          </form-group>
        </div>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn
            color="primary"
            class="btn-primary-custom"
            :loading="loadingFile"
            v-if="permissionButton('ROLE_FILES_UPDATE') || permissionButton('ROLE_FILES_LITE_UPDATE')"
            @click="saveFile"
            >{{ $t(formFile.id ? 'save' : 'upload') }}</v-btn
          >
          <v-btn color="primary" :disabled="loadingFile === true" text @click="clearFileForm">{{ $t('cancel') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog Information -->
    <v-dialog v-model="dialogInformation" width="500">
      <v-card>
        <v-card-title class="headline secondary white--text"> {{ $t('content.file_information') }} </v-card-title>
        <div class="pa-4">
          <div class="mb-4">{{ $t('content.file.name') }} : {{ $get(information, 'title', '-') }}</div>
          <div class="mb-4">{{ $t('content.file.date_created') }} : {{ $get(information, 'createdAt', '-') }}</div>
          <div class="mb-4">{{ $t('content.file.locked') }} : {{ $get(information, 'lock', false) ? $t('lock') : $t('unlock') }}</div>
          <div class="mb-4">{{ $t('content.file.size') }} : {{ $get(information, 'size', '-') }}</div>
          <div class="">{{ $t('content.file.can_edit') }} : {{ $get(information, 'owner', false) ? $t('yes') : $t('no') }}</div>
        </div>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn color="primary" class="btn-primary-custom" @click="dialogInformation = !dialogInformation">{{ $t('close') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog Delete -->
    <v-dialog v-model="dialogDelete" persistent max-width="290">
      <v-card>
        <v-card-title class="headline">{{ $t('delete_title') }}</v-card-title>
        <v-card-text>{{ $t('delete_message') }}</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="dialogDelete = false">{{ $t('disagree') }}</v-btn>
          <v-btn
            text
            :loading="loadingDelete"
            v-if="permissionButton('ROLE_FILES_DELETE') || permissionButton('ROLE_FILES_LITE_DELETE')"
            @click="removeTree"
            >{{ $t('aggree') }}</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog Upload Progress -->
    <v-dialog v-model="dialogProgressUpload" persistent max-width="400">
      <v-card>
        <v-card-title class="headline">{{ $t('upload_file') }}</v-card-title>
        <div class="pa-8 pt-4">
          <div class="d-flex align-center">
            <svg-icon
              icon="note"
              class="primary--text mr-8"
              :style="{
                flex: 'none',
                transform: 'scale(1.5)'
              }"
            />
            <div class="d-flex row align-center mr-3" :style="{ flex: 1 }">
              <div>{{ progressUpload }} %</div>
              <v-progress-linear :value="progressUpload" />
            </div>
            <v-btn text @click="dialogCancelUpload = true" class="btn-action-custom"><svg-icon icon="delete" class="red--text"/></v-btn>
          </div>
        </div>
      </v-card>
    </v-dialog>

    <!-- Dialog Delete -->
    <v-dialog v-model="dialogCancelUpload" persistent max-width="290">
      <v-card>
        <v-card-title class="headline">{{ $t('cancel_upload_title') }}</v-card-title>
        <v-card-text>{{ $t('cancel_upload_message') }}</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="dialogCancelUpload = false">{{ $t('disagree') }}</v-btn>
          <v-btn text :loading="loadingDelete" @click="cancelUpload">{{ $t('aggree') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import EventBus from '@/plugins/eventBus';
import { omit, debounce, map } from 'lodash';
import { mapGetters } from 'vuex';
import { maxLength, required } from 'vuelidate/lib/validators';
import BaseTitle from '@/components/base/Title';
import TableHeader from '@/components/table/TableHeader';
import FileViewer from '@/components/FileViewer';
import FileItem from '@/components/FileItem';
import { TreeViewComponent as Tree } from '@syncfusion/ej2-vue-navigations';
import { filename, fileSize, fileType } from '@/validators';

const extAllowed = ['mp4', 'mp3', 'pptx', 'ppt', 'doc', 'docx', 'xls', 'xlsx', 'pdf', 'jpg', 'png', 'jpeg', 'sb3'];

export default {
  name: 'FilesIndex',
  components: {
    BaseTitle,
    TableHeader,
    FileViewer,
    Tree
  },
  props: {
    editable: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    ...mapGetters({
      folders: 'Files/folders',
      files: 'Files/files',
      progressUpload: 'Files/progressUpload',
      currentRole: 'Auth/currentRole',
      permission: 'Auth/permission',
      user: 'Auth/user',
      nameCourse: 'CourseList/nameCourse'
    }),
    tableHeader() {
      return [
        { text: this.$t('table.name'), value: 'title', width: 200 },
        { text: '', value: 'lock', sortable: false },
        { text: this.$t('table.date_created'), value: 'createdAt', sortable: false },
        { text: this.$t('table.date_modified'), value: 'updatedAt', sortable: false },
        { text: this.$t('table.size'), value: 'size', sortable: false },
        { text: this.$t('table.action'), value: 'action', sortable: false }
      ];
    },
    lockItems() {
      return [
        { text: this.$t('unlock'), value: false },
        { text: this.$t('lock'), value: true }
      ];
    },
    breadcrumbs() {
      const breadcrumbs = [];

      // Handle if folder other not exists
      if (this.otherFolderId) {
        breadcrumbs.push({ label: 'home', id: this.otherFolderId });
      }

      if (this.otherFolderId !== this.selectedFolderId) {
        breadcrumbs.push({
          label: this.directories.find(dir => dir.id === this.selectedFolderId)?.title,
          id: this.selectedFolderId
        });
      }
      return breadcrumbs;
    },
    filesWithBack() {
      if (this.breadcrumbs?.[1]) {
        return [{ isBack: true, lock: null }, ...this.files];
      }
      return this.files;
    },
    lockAt() {
      return this.formFile.lock != null && this.formFile.scheduled ? `${this.unlockDate} ${this.unlockTime}` : '';
    }
  },
  validations() {
    return {
      formFolder: {
        name: { required, foldername: filename, maxLength: maxLength(255) }
      },
      formFile: {
        fileName: {
          required,
          filename,
          maxLength: maxLength(255),
          nameDuplicate(val) {
            if (!this.formFile.owner) return true;
            const el = document.querySelector(`.e-list-item[data-uid="${this.formFile.id}"]`);
            if (el) {
              return !Array.from(el.parentNode.children)
                .filter(elx => elx.dataset.uid.indexOf('File-') !== -1 && elx.dataset.uid !== this.formFile.id)
                .map(elx => {
                  const ext = this.getTreeDataset(elx).ext?.toLowerCase();
                  return `${elx.textContent?.toLowerCase()}${ext}`;
                })
                .includes(`${val?.toLowerCase()}${this.getTreeDataset(el).ext?.toLowerCase()}`);
            }
          }
        },
        lock: { required },
        ...(this.formFile.id
          ? null
          : {
              file: {
                required,
                fileSize: fileSize(2000),
                fileType: fileType(extAllowed)
              }
            })
      },
      unlockDate: this.formFile.lock !== null && this.formFile.scheduled ? { required } : {},
      unlockTime: this.formFile.lock !== null && this.formFile.scheduled ? { required } : {}
    };
  },
  sockets: {
    connect() {
      console.log('socket connected');
    },
    updateFile({ classId, courseId, classSectionId }) {
      console.info(classId, courseId, classSectionId);
      const { classId: currentClassId, courseId: currentCourseId, classSectionId: currentClassSectionId } = this.$route.params;
      if (classId == currentClassId && courseId == currentCourseId && classSectionId == currentClassSectionId) {
        this.dialogInformation = false;
        this.dialogPreview = false;
        this.$store.dispatch('general/showSuccessNotification', { message: 'File Status Has Updated' });
        setTimeout(() => {
          this.getFolders();
        }, 400);
      } else {
        this.dialogInformation = false;
        this.dialogPreview = false;
        this.$store.dispatch('general/showSuccessNotification', { message: 'File Status Has Updated' });
        setTimeout(() => {
          this.getFolders();
        }, 400);
      }
    }
  },
  data: () => ({
    dataClass: '',
    preview: {},
    table: {
      loading: false,
      options: {}
    },
    tree: {
      fields: {
        dataSource: [],
        id: 'id',
        text: 'title',
        child: 'files'
      },
      template: () => ({ template: FileItem })
    },
    formFile: {
      id: null,
      fileName: null,
      lock: null,
      file: null,
      owner: null,
      scheduled: false
    },
    formFolder: {
      id: null,
      name: null
    },
    search: {
      folder: null
    },
    pickerDate: false,
    pickerTime: false,
    unlockDate: null,
    unlockTime: null,
    lockUpdate: true,
    loadingTree: false,
    loadingFolder: false,
    loadingFile: false,
    loadingDelete: false,
    dialogFolder: false,
    dialogFile: false,
    dialogCancelUpload: false,
    dialogInformation: false,
    dialogDelete: false,
    dialogPreview: false,
    dialogProgressUpload: false,
    selectedFolderId: null,
    selectedDeleteObj: null,
    otherFolderId: null,
    expandIds: null,
    information: {},
    directories: [],
    areaActive: false,
    eventThrottled: false,
    titleData: ''
  }),
  watch: {
    ['table.options']() {
      this.getFiles();
    },
    ['formFile.lock']() {
      // this.formFile.scheduled = false;
      // this.unlockDate = null;
      // this.unlockTime = null;
    }
  },
  methods: {
    permissionButton(push) {
      return this.permission.some(p => p === push) ?? false;
    },
    // Folder
    getFolders() {
      const { tree } = this.$refs;
      this.$store.commit('Files/CLEAR_FILES');
      this.loadingTree = true;
      const { classId, courseId } = this.$route.params;
      const temp = Array.from(document.querySelectorAll('.e-list-item[aria-expanded="true"]')).map(el => el.dataset.uid);
      return new Promise(resolve => {
        this.$store
          .dispatch('Files/fetchFolders', {
            search: this.search.folder,
            classId,
            courseId,
            classSectionId: this.$route.params.classSectionId ? this.$route.params.classSectionId : ''
          })
          .then(folders => {
            folders.forEach(res => (res.folder = true));
            this.directories = folders
              .reduce((acc, el) => {
                acc.push(omit(el, 'files'));
                acc.push(...el.files);
                return acc;
              }, [])
              .filter(folder => folder?.folder);

            this.lockUpdate = true;
            const treeParent = tree.$el.querySelector('.e-list-parent');
            if (treeParent) {
              const deleted = map(treeParent.children, 'dataset.uid');
              if (deleted.length) {
                tree.removeNodes(deleted);
                tree.fields.dataSource = [];
              }
            }
            this.parseTree(folders);
            tree.collapseAll();

            // Needed Timeout
            setTimeout(() => tree.expandAll(temp, 1), 100);
            this.lockUpdate = false;

            if (this.selectedFolderId) {
              this.getTreeElement(this.selectedFolderId)?.classList.add('e-active');
              this.getFiles();
            }
            resolve();
          })
          .finally(() => (this.loadingTree = false));
      });
    },
    addFolder() {
      this.clearFolderForm();
      this.dialogFolder = true;
    },
    saveFolder() {
      this.$v.formFolder.$touch();
      if (this.$v.formFolder.$invalid) return false;

      this.loadingFolder = true;
      const { classId, courseId } = this.$route.params;
      const isEdit = this.formFolder.id;
      const payload = isEdit
        ? { folderId: this.formFolder.id, folderName: this.formFolder.name }
        : {
            folderName: this.formFolder.name,
            classId,
            courseId,
            classSectionId: this.$route.params.classSectionId ? this.$route.params.classSectionId : ''
          };

      this.$store
        .dispatch(`Files/${isEdit ? 'updateFolder' : 'createFolder'}`, payload)
        .then(() => {
          this.getFolders();
          this.clearFolderForm();
        })
        .finally(() => {
          this.loadingFolder = false;
          this.lockUpdate = false;
        });
    },
    cancelFolder() {
      this.clearFolderForm();
      this.dialogFolder = false;
    },
    clearFolderForm() {
      this.dialogFolder = false;
      this.$v.formFolder.$reset();
      this.$set(this, 'formFolder', { id: null, name: null });
    },
    searchFolder: debounce(function(e) {
      if ([40, 38].includes(e.keyCode ? e.keyCode : e.which)) return;
      this.getFolders();
    }, 500),

    // File
    openDialogFile() {
      this.dialogFile = true;
      this.formFile.scheduled = false;
      this.unlockDate = null;
      this.unlockTime = null;
    },
    getFiles() {
      if (!this.selectedFolderId) return;
      this.table.loading = true;
      this.$store
        .dispatch('Files/fetchFiles', { folderId: this.selectedFolderId, ...this.table.options })
        .finally(() => (this.table.loading = false));
    },
    saveFile() {
      this.$v.formFile.$touch();
      this.$v.unlockDate.$touch();
      this.$v.unlockTime.$touch();
      if (this.$v.formFile.$invalid || this.$v.unlockDate.$invalid || this.$v.unlockTime.$invalid) return false;

      const isEdit = this.formFile.id;
      this.loadingFile = true;
      this.dialogProgressUpload = !isEdit;
      this.$store
        .dispatch(`Files/${isEdit ? 'updateFile' : 'upload'}`, {
          ...this.formFile,
          id: this.formFile.id?.replace('File-', '') ?? null,
          folderId: this.selectedFolderId,
          lockAt: this.lockAt
        })
        .then(() => {
          this.getFolders();
          this.clearFileForm();
        })
        .finally(() => {
          this.$store.commit('Files/DONE_PROGRESS');
          setTimeout(() => {
            this.loadingFile = false;
            this.dialogProgressUpload = false;
            this.$store.commit('Files/CLEAR_PROGRESS');
          }, 120);
        });
    },
    clearFileForm() {
      this.dialogFile = false;
      this.$v.formFile.$reset();
      this.$set(this, 'formFile', { id: null, fileName: null, file: null, lock: null, scheduled: false });
    },
    viewFile(item) {
      this.dialogInformation = true;
      this.information = item;
    },
    cancelUpload() {
      this.dialogCancelUpload = false;
      this.$store.dispatch('Files/cancelUpload');
    },

    // Handle Drag & Drop
    dragover(e) {
      e.preventDefault();
      if (!this.editable) return;

      this.areaActive = true;
    },
    dragleave(e) {
      e.preventDefault();
      if (!this.editable) return;
      if (this.eventThrottled) return;
      this.areaActive = false;
      this.eventThrottled = true;
      setTimeout(() => (this.eventThrottled = false), 100);
    },
    drop(e) {
      e.preventDefault();
      if (!this.editable) return;

      this.areaActive = false;
      const files = e.dataTransfer.files;

      // Validate File Length
      if (files.length > 1) {
        this.$store.dispatch('general/showErrorNotification', { message: this.$t('content.file.file_drop_max') });
        return;
      }

      const [file] = files;
      // Validate File Type
      if (
        !extAllowed.includes(
          file.name
            .split('.')
            .pop()
            .toLowerCase()
        )
      ) {
        this.$store.dispatch('general/showErrorNotification', { message: this.$t('validations.fileType') });
        return;
      }

      // Validate File Name Exists
      if (this.files.some(f => `${f.title.toLowerCase()}` === file.name.toLowerCase())) {
        this.$store.dispatch('general/showErrorNotification', { message: this.$t('validations.nameDuplicate') });
        return;
      }

      // Validata File Size
      if (+(file.size / (1024 * 1024)).toFixed(0) > 2000) {
        this.$store.dispatch('general/showErrorNotification', { message: this.$t('validations.fileSize', { attribute: '', max: '2000' }) });
        return;
      }

      // Upload Here!
      this.dialogProgressUpload = true;
      let filename = file.name.split('.').shift();
      filename = this.cleanFileName(filename);
      this.$store
        .dispatch('Files/upload', { folderId: this.selectedFolderId, fileName: filename, file, lock: false })
        .then(() => {
          this.getFolders();
        })
        .finally(() => {
          this.$store.commit('Files/DONE_PROGRESS');
          setTimeout(() => {
            this.dialogProgressUpload = false;
            this.$store.commit('Files/CLEAR_PROGRESS');
          }, 120);
        });
    },

    // File Viewer
    previewFile(e, { item }) {
      this.dialogPreview = true;
      this.$set(this, 'preview', item);
    },
    hidePreview() {
      this.dialogPreview = null;
      this.$set(this, 'preview', {});
    },
    handlerFile(item) {
      if (item.isBack) {
        this.selectedFolderId = this.otherFolderId;
        this.getFiles();
      }
    },
    handlerFileFromBreadcrumb({ id }) {
      this.selectedFolderId = id;
      this.getFiles();
    },

    // Tree
    dragStart(args) {
      const data = this.$refs.tree.getTreeData(args.draggedNode)?.[0];
      if (data?.folder) {
        args.cancel = true;
      } else if (!data.owner) {
        args.cancel = true;
      }
    },
    dragStop(args) {
      let cancel = false;

      if (args?.droppedNodeData) {
        // Validation if type is file can't drop to file
        if (args.droppedNodeData.id.indexOf('File') === 0) {
          cancel = true;
        }

        // Validation outside folder
        if (args.dropLevel == 1) {
          const registered = Array.from(args.droppedNode.parentNode.children)
            .reduce((acc, el) => {
              acc.push(this.$refs.tree.getTreeData(el)[0]);
              return acc;
            }, [])
            .filter(f => typeof f.id === 'string' && f.id.indexOf('File') !== -1 && f.id !== args?.draggedNodeData?.id)
            .find(el => `${el.title}` === `${args.draggedNodeData.text}${this.getTreeDataset(args.draggedNode)}`);

          if (registered) {
            this.$store.dispatch('general/showErrorNotification', { message: this.$t('alert.alert_duplicate') });
            cancel = true;
          }
        }

        // Validation inside folder
        if (args.dropLevel > 1) {
          const children = args.droppedNode.querySelectorAll('.file-name-text');
          const find = Array.from(children).find(el => {
            return `${args.draggedNodeData.text}${this.getTreeDataset(args.draggedNode)}` === `${el.textContent}`;
          });
          if (find) {
            this.$store.dispatch('general/showErrorNotification', { message: this.$t('alert.alert_duplicate') });
            cancel = true;
          }
        }
      } else {
        cancel = true;
      }
      args.cancel = cancel;
    },
    updateTree(payload) {
      if (this.lockUpdate) return;
      const newPayload = this.transformPayload(payload);
      this.$store.dispatch('Files/updateTree', newPayload);
    },
    transformPayload({ data }) {
      let newPayload = [];
      const otherFolder = { folderId: this.otherFolderId, fileId: [] };
      data.forEach(p => {
        if (p?.folder) {
          newPayload.push({ folderId: p.id, fileId: [] });
        } else {
          if (p?.parentID) {
            const index = newPayload.findIndex(pp => pp.folderId == p.parentID);
            if (index !== -1) {
              newPayload[index].fileId.push(+p.id.replace('File-', ''));
            }
          } else {
            otherFolder.fileId.push(+p.id.replace('File-', ''));
          }
        }
      });
      newPayload.push(otherFolder);
      return newPayload;
    },
    renameTree({ newText, node }) {
      const data = this.$refs.tree.getTreeData(node)?.[0];
      if (data.folder) {
        this.$store.dispatch('Files/updateFolder', { folderId: data.id, folderName: newText });
      } else {
        const folderId = this.$refs.tree.getNode('' + data.id).parentID;
        const fileId = data.id.replace('File-', '');
        this.$store.dispatch('Files/updateFile', { folderId, fileId, fileName: newText, lock: data.lock });
      }
    },
    confirmDelete() {
      this.dialogDelete = true;
    },
    removeTree() {
      const { folder, id } = this.selectedDeleteObj;
      const deletedId = folder ? id : id.replace('File-', '');
      this.loadingDelete = true;
      this.$store
        .dispatch(`Files/${folder ? 'removeFolder' : 'removeFile'}`, deletedId)
        .then(() => {
          this.getFolders();
          this.dialogDelete = false;
          this.selectedDeleteObj = {};
          this.selectedFolderId = this.otherFolderId;
        })
        .finally(() => (this.loadingDelete = false));
    },
    parseTree(items) {
      const { tree } = this.$refs;
      items.forEach(folder => {
        folder.role = this.currentRole;
        if (!['others', 'other'].includes(folder.title.toLowerCase())) {
          tree.addNodes([omit(folder, 'files')], null, null);
          tree.fields.dataSource.push(folder);
          folder.files.forEach(file => {
            file.role = this.currentRole;
            file.id = 'File-' + file.id;
            tree.addNodes([file], '' + folder.id, null);
            tree.fields.dataSource.push(file);
          });
        } else {
          this.otherFolderId = folder.id;
          folder.files.forEach(file => {
            file.role = this.currentRole;
            file.id = 'File-' + file.id;
            tree.addNodes([file], null, null);
            tree.fields.dataSource.push(file);
          });
        }
      });
    },
    getExpandIds() {
      const expandIds = Array.from(document.querySelectorAll('.e-list-item[aria-expanded="true"]')).map(el => el.dataset.uid);
      const { tree } = this.$refs;
      tree.expandAll(expandIds, 1);
      return expandIds;
    },
    getTreeElement(id) {
      return document.querySelector(`.e-list-item[data-uid="${id}"`);
    },
    getTreeDataset(el) {
      return el.querySelector('.file-name-text').dataset;
    },

    // Util
    cleanFileName(str) {
      return str
        .match(/[\w\d\s\\,\\_\\(\\)-]*/gm)
        .reduce((acc, row) => {
          acc += row;
          return acc;
        }, '')
        .trim();
    },

    getDataClass() {
      if (!this.$route.params.classSectionId) {
        this.$store
          .dispatch('Class/fetchClassroomCourseName', { classId: this.$route.params.classId, courseId: this.$route.params.courseId })
          .then(res => {
            if (this.user.cerdasTypeId === 2 || this.user.cerdasTypeId === null) {
              this.$store.dispatch('SchoolClass/fetchDetail', this.$route.params.classId).then(resa => {
                this.dataClass = resa;
                this.titleData = res + ` ( ${resa.yearPeriode} ) `;
              });
            } else {
              this.$store.dispatch('Class/fetchListLiteDetail', this.$route.params.classId).then(resa => {
                this.dataClass = resa;
                this.titleData = res + ` ( ${resa.yearPeriode} ) `;
              });
            }
          });
      } else {
        this.$store.dispatch('ClassSection/getListDetail', { classSectionId: this.$route.params.classSectionId }).then(res => {
          if (this.user.cerdasTypeId === 2 || this.user.cerdasTypeId === null) {
            this.$store.dispatch('SchoolClass/fetchDetail', this.$route.params.classId).then(resa => {
              this.dataClass = resa;
              this.titleData = res.classroomName + ` ${res.numberClass}` + ` ( ${resa.yearPeriode} ) `;
            });
          }
        });
      }
    }
  },
  created() {
    this.getDataClass();
    // EventBus from children to parent
    EventBus.$on('treeItemClick', data => {
      if (data?.folder) {
        this.selectedFolderId = data?.id;
        this.getFiles();
      } else {
        this.dialogPreview = true;
        this.$set(this, 'preview', data);
      }
    });

    EventBus.$on('treeItemRemove', data => {
      this.selectedDeleteObj = data;
      this.confirmDelete();
    });

    EventBus.$on('treeItemEdit', data => {
      if (data?.folder) {
        this.dialogFolder = true;
        this.$set(this, 'formFolder', {
          id: data.id,
          name: data.title
        });
      } else {
        this.dialogFile = true;
        this.$set(this, 'formFile', {
          id: data.id,
          fileName: data.title,
          lock: data.lock,
          file: null,
          owner: data.owner,
          scheduled: data.scheduled
        });
        this.unlockDate = data.lockAt ? data.lockAt.split(' ')[0] : null;
        this.unlockTime = data.lockAt ? data.lockAt.split(' ')[1] : null;
      }
    });
  },
  mounted() {
    this.getFolders().then(() => {
      this.selectedFolderId = this.otherFolderId;
      this.getFiles();
    });
  }
};
</script>

<style lang="scss">
@import '~@syncfusion/ej2-base/styles/material.css';
@import '~@syncfusion/ej2-vue-navigations/styles/material.css';

.e-treeview {
  > .e-ul {
    padding: 10px 0 !important;
  }
}

.e-list-text {
  width: 100% !important;
}

.e-list-item.e-hover:hover {
  > .e-text-content > .e-list-text > .file-wrapper > .file-action {
    display: flex;
  }
}

.file-breadcrumb {
  display: flex;
  padding: 0 !important;
  margin: 0 !important;
  list-style: none;

  li {
    padding: 3px 10px 3px 20px;
    position: relative;
    border-radius: 3px;
    cursor: pointer;
    transition: all 250ms cubic-bezier(0.645, 0.045, 0.355, 1);

    &:first-child {
      padding: 3px 10px 3px 10px;
    }

    &:hover {
      background: #eee;
    }

    &::after {
      content: '/';
      position: absolute;
      right: -10px;
    }

    &:last-child {
      &::after {
        content: '';
      }
      pointer-events: none;
      opacity: 0.8;
    }
  }
}

.area-active {
  position: relative;

  &::after {
    content: attr(data-text);
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: #fff;
    font-weight: bold;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10;
  }

  .table-custom {
    thead tr,
    tbody tr {
      z-index: -1;
      pointer-events: none !important;
    }
  }

  & * {
    pointer-events: none !important;
  }
}
</style>
